import { createLogic } from 'redux-logic';
import { normalize, schema } from 'normalizr';
import { hosts } from 'settings';
import { memberStorage } from 'utils';
import { decorateRecipes } from 'decorators';

import {
  camelCase,
  flatMap,
  flow,
  filter,
  find,
  findKey,
  map,
  mapValues,
  reduce,
  reject,
  some,
} from 'lodash';

// Action Types
const FETCH = '@@app/meal-plan/FETCH';
const FETCH_SUCCESS = '@@app/meal-plan/FETCH_SUCCESS';

const FETCH_ACTIVE = '@@app/meal-plan/FETCH_ACTIVE';
const FETCH_ACTIVE_SUCCESS = '@@app/meal-plan/FETCH_ACTIVE_SUCCESS';

const FETCH_SWAPS = '@@app/meal-plan/FETCH_SWAPS';
const FETCH_SWAPS_SUCCESS = '@@app/meal-plan/FETCH_SWAPS_SUCCESS';

const FETCH_ALL = '@@app/meal-plan/FETCH_ALL';
const FETCH_ALL_SUCCESS = '@@app/meal-plan/FETCH_ALL_SUCCESS';

const FETCH_WEEK = '@@app/meal-plan/FETCH_WEEK';
const FETCH_WEEK_SUCCESS = '@@app/meal-plan/FETCH_WEEK_SUCCESS';

const UPDATE = '@@app/meal-plan/UPDATE';
const UPDATE_SUCCESS = '@@app/meal-plan/UPDATE_SUCCESS';

const CLONE = '@@app/meal-plan/CLONE';
const CLONE_SUCCESS = '@@app/meal-plan/CLONE_SUCCESS';

const CREATE = '@@app/meal-plan/CREATE';
const CREATE_SUCCESS = '@@app/meal-plan/CREATE_SUCCESS';

const DELETE = '@@app/meal-plan/DELETE';
const DELETE_SUCCESS = '@@app/meal-plan/DELETE_SUCCESS';

const RESET = '@@app/meal-plan/RESET';
const RESET_SUCCESS = '@@app/meal-plan/RESET_SUCCESS';

const ACTIVATE = '@@app/meal-plan/ACTIVATE';
const ACTIVATE_SUCCESS = '@@app/meal-plan/ACTIVATE_SUCCESS';

const DEACTIVATE = '@@app/meal-plan/DEACTIVATE';
const DEACTIVATE_SUCCESS = '@@app/meal-plan/DEACTIVATE_SUCCESS';

const SWAP = '@@app/meal-plan/SWAP';
const SWAP_REVERT = '@@app/meal-plan/SWAP_REVERT';
const SWAP_SUCCESS = '@@app/meal-plan/SWAP_SUCCESS';

const CREATE_MEAL = '@@app/meal-plan/CREATE_MEAL';
const CREATE_MEAL_SUCCESS = '@@app/meal-plan/CREATE_MEAL_SUCCESS';

const DELETE_MEAL = '@@app/meal-plan/DELETE_MEAL';
const DELETE_MEAL_SUCCESS = '@@app/meal-plan/DELETE_MEAL_SUCCESS';

const ERROR = '@@app/meal-plan/ERROR';
const CANCEL = '@@app/meal-plan/CANCEL';

// Initial State
export const initialState = {
  isActivating: false,
  isCreating: false,
  isLoading: false,
  isSwapping: false,
  isSwappingDay: -1,
  entities: {
    mealPlans: {},
    recipes: null,
    byVoome: null,
    mine: null,
    current: null,
  },
  result: null,
  active: null,
  level: 0,
};

// Normalize
const mealPlanSchema = () =>
  new schema.Entity('mealPlans', {
    recipes: [new schema.Entity('recipes')],
  });

const normalizeMealPlan = data => {
  return data.id
    ? normalize(
        {
          ...data,
          recipes: decorateRecipes(data.recipes),
        },
        mealPlanSchema()
      )
    : {
        entities: {},
        result: null,
      };
};

const normalizeMealPlans = (response, key) => {
  const normalized = normalize(response.data, [mealPlanSchema()]);
  const { result, entities } = normalized;

  const pagination = response.pagination
    ? response.pagination
    : {
        page: 1,
        totalPages: 1,
        total: result.length,
      };

  return {
    ...normalized,
    key,
    entities: {
      ...entities,
      [key]: {
        [pagination.page]: {
          ...pagination,
          result,
        },
      },
    },
  };
};

// Observables
const fetchLogic = createLogic({
  type: FETCH,
  processOptions: {
    successType: FETCH_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { level, mealPlanId } = action.payload;
    const endpoint = `/v1/voome/meal-plans/${mealPlanId}`;
    const params = { level };
    const onSuccess = response => normalizeMealPlan(response.data);
    return httpClient.get(endpoint, { params }).then(onSuccess);
  },
});

const fetchActiveLogic = createLogic({
  type: FETCH_ACTIVE,
  debounce: 500,
  latest: true,
  processOptions: {
    successType: FETCH_ACTIVE_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const endpoint = `/v1/voome/meal-plans/active`;
    const onSuccess = response => normalizeMealPlan(response.data);
    return httpClient.get(endpoint).then(onSuccess);
  },
});

const fetchSwapsLogic = createLogic({
  type: FETCH_SWAPS,
  processOptions: {
    successType: FETCH_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { level, mealPlanId, memberId } = action.payload;
    const endpoint = `/v2/voome/members/${memberId}/meal-plans/${mealPlanId}/swapped-meals`;
    const params = { level };
    const onSuccess = ({ data }) =>
      normalizeMealPlan({ ...data, hasSwaps: mealsHasSwaps(mealsFlat(data)) });

    return httpClient.get(endpoint, { params }).then(onSuccess);
  },
});

const fetchWeekLogic = createLogic({
  type: FETCH_WEEK,
  process({ action, httpClient }, dispatch, done) {
    const { subscriptionId, weekId, level, memberId } = action.payload;
    const endpoint = `/v1/voome/meal-plans/mine`;
    const params = { subscriptionId, weekId };

    const onSuccess = ({ data }) => {
      const mealPlanId = data.length ? data[0].id : action.payload.mealPlanId;
      dispatch({ type: FETCH_WEEK_SUCCESS, payload: { mealPlanId, weekId } });
      dispatch(fetchMealPlanSwaps(mealPlanId, memberId, level));
    };

    const onFail = () => dispatch({ type: ERROR });

    return httpClient
      .get(endpoint, { params })
      .then(onSuccess)
      .catch(onFail)
      .then(done);
  },
});

const fetchAllLogic = createLogic({
  type: FETCH_ALL,
  processOptions: {
    successType: FETCH_ALL_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { key, page, subscriptionId, weekId } = action.payload;
    const endpoint = `/v1/voome/meal-plans/${key}`;
    const params = { page, subscriptionId, weekId };
    const onSuccess = response => normalizeMealPlans(response, camelCase(key));
    return httpClient.get(endpoint, { params }).then(onSuccess);
  },
});

const updateLogic = createLogic({
  type: UPDATE,
  processOptions: {
    successType: UPDATE_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { mealPlanId, mealPlan } = action.payload;
    const endpoint = `/v1/voome/meal-plans/${mealPlanId}`;
    return httpClient.put(endpoint, { mealPlan }).then(() => action.payload);
  },
});

const resetLogic = createLogic({
  type: RESET,
  processOptions: {
    successType: RESET_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { mealPlanId } = action.payload;
    const endpoint = `/v1/voome/meal-plans/${mealPlanId}/meal-plan-recipe-swaps/reset`;
    return httpClient.delete(endpoint);
  },
});

const revertLogic = createLogic({
  type: SWAP_REVERT,
  processOptions: {
    successType: SWAP_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    return httpClient.delete(
      `/v1/voome/meal-plan-recipe-swaps/${action.payload.swappedMealId}`
    );
  },
});

const swapLogic = createLogic({
  type: SWAP,
  processOptions: {
    successType: SWAP_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { mealPlanRecipeId, recipeContainerId } = action.payload;
    const endpoint = '/v1/voome/meal-plan-recipe-swaps';
    const params = { mealPlanRecipeId, recipeContainerId };
    return httpClient.post(endpoint, params);
  },
});

const swapSuccessLogic = createLogic({
  type: [RESET_SUCCESS, SWAP_SUCCESS],
  process({ action, getState }, dispatch, done) {
    const { result, level } = getState().mealPlan;
    const { memberId } = memberStorage.get();
    dispatch(fetchMealPlanSwaps(result, memberId, level));
    done();
  },
});

const activateLogic = createLogic({
  type: ACTIVATE,
  processOptions: {
    successType: ACTIVATE_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { mealPlanId } = action.payload;
    const endpoint = `/v1/voome/meal-plans/${mealPlanId}/activate`;
    const onSuccess = () => action.payload;
    return httpClient.put(endpoint).then(onSuccess);
  },
});

const deactivateLogic = createLogic({
  type: DEACTIVATE,
  processOptions: {
    successType: DEACTIVATE_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { mealPlanId } = action.payload;
    const endpoint = `/v1/voome/meal-plans/${mealPlanId}/deactivate`;
    const onSuccess = () => ({ mealPlanId: null });
    return httpClient.put(endpoint).then(onSuccess);
  },
});

const cloneLogic = createLogic({
  type: CLONE,
  processOptions: {
    successType: CLONE_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { mealPlanId, subscriptionId, weekId } = action.payload;
    const endpoint = `/v1/voome/meal-plans/${mealPlanId}/clone`;
    const params = { subscriptionId, weekId };
    const onSuccess = response => normalizeMealPlan(response.data);
    return httpClient.get(endpoint, { params }).then(onSuccess);
  },
});

const createMealPlanLogic = createLogic({
  type: CREATE,
  processOptions: {
    successType: CREATE_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { subscriptionId } = action.payload;
    const endpoint = '/v1/voome/meal-plans';
    const params = { subscriptionId };
    const onSuccess = response => normalizeMealPlan(response.data);
    return httpClient.post(endpoint, params).then(onSuccess);
  },
});

const deleteMealPlanLogic = createLogic({
  type: DELETE,
  processOptions: {
    successType: DELETE_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const endpoint = `/v1/voome/meal-plans/${action.payload.mealPlanId}`;
    const onSuccess = () => action.payload;
    return httpClient.delete(endpoint).then(onSuccess);
  },
});

const createMealLogic = createLogic({
  type: CREATE_MEAL,
  processOptions: {
    successType: CREATE_MEAL_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { day, leftOvers, mealPlanId, recipeId } = action.payload;
    const endpoint = `/v1/voome/meal-plans/${mealPlanId}/create-meal`;
    const params = { leftOvers, day, recipeId };
    const onSuccess = response => normalizeMealPlan(response.data);
    return httpClient.post(endpoint, params).then(onSuccess);
  },
});

const deleteMealLogic = createLogic({
  type: DELETE_MEAL,
  processOptions: {
    successType: DELETE_MEAL_SUCCESS,
    failType: ERROR,
  },
  process({ action, httpClient }) {
    const { mealPlanId, mealPlanRecipeId } = action.payload;
    const endpoint = `/v1/voome/meal-plans/${mealPlanId}/destroy-meal`;
    const params = { mealPlanRecipeId };
    const onSuccess = () => action.payload;
    return httpClient.delete(endpoint, { params }).then(onSuccess);
  },
});

export const mealPlanLogic = [
  activateLogic,
  deactivateLogic,
  createMealLogic,
  deleteMealLogic,
  cloneLogic,
  createMealPlanLogic,
  deleteMealPlanLogic,
  fetchLogic,
  fetchActiveLogic,
  fetchAllLogic,
  fetchSwapsLogic,
  fetchWeekLogic,
  resetLogic,
  revertLogic,
  swapLogic,
  swapSuccessLogic,
  updateLogic,
];

// Action Creators
export const fetchMealPlan = (mealPlanId, level) => {
  return {
    type: FETCH,
    payload: { mealPlanId, level },
  };
};

export const fetchMealPlanSwaps = (mealPlanId, memberId, level) => {
  return {
    type: FETCH_SWAPS,
    payload: { mealPlanId, memberId, level },
  };
};

export const fetchMealPlanForWeek = payload => {
  return { type: FETCH_WEEK, payload };
};

export const fetchMealPlansCurrent = subscriptionId => {
  const key = 'current';
  return { type: FETCH_ALL, payload: { key, subscriptionId } };
};

export const fetchMealPlansMine = (subscriptionId, page = 1, weekId) => {
  const key = 'mine';
  return { type: FETCH_ALL, payload: { key, page, subscriptionId, weekId } };
};

export const fetchMealPlansByVoome = (subscriptionId, page = 1) => {
  const key = 'by-voome';
  return { type: FETCH_ALL, payload: { key, page, subscriptionId } };
};

export const fetchMealPlanActive = () => {
  return { type: FETCH_ACTIVE };
};

export const cloneMealPlan = (mealPlanId, subscriptionId, weekId) => {
  return {
    type: CLONE,
    payload: { mealPlanId, subscriptionId, weekId },
  };
};

export const createMealPlan = subscriptionId => {
  return {
    type: CREATE,
    payload: { subscriptionId },
  };
};

export const deleteMealPlan = mealPlanId => {
  return {
    type: DELETE,
    payload: { mealPlanId },
  };
};

export const resetMealPlan = mealPlanId => {
  return { type: RESET, payload: { mealPlanId } };
};

export const updateMealPlan = (mealPlanId, mealPlan) => {
  return { type: UPDATE, payload: { mealPlanId, mealPlan } };
};

export const activateMealPlan = mealPlanId => {
  return { type: ACTIVATE, payload: { mealPlanId } };
};

export const deactivateMealPlan = mealPlanId => {
  return { type: DEACTIVATE, payload: { mealPlanId } };
};

export const swapMeal = (mealPlanRecipeId, recipeContainerId) => {
  return { type: SWAP, payload: { mealPlanRecipeId, recipeContainerId } };
};

export const swapRemove = mealId => {
  return swapMeal(mealId, 0);
};

export const swapRevert = swappedMealId => {
  return { type: SWAP_REVERT, payload: { swappedMealId } };
};

export const createMeal = (day, mealPlanId, recipeId, leftOvers) => {
  return {
    type: CREATE_MEAL,
    payload: { day, leftOvers, mealPlanId, recipeId },
  };
};

export const deleteMeal = (mealPlanId, mealPlanRecipeId, day) => {
  return {
    type: DELETE_MEAL,
    payload: { mealPlanId, mealPlanRecipeId, day },
  };
};

// Helpers
const mealsFlat = mealPlan => flatMap(mealPlan.days, 'meals');
const mealsWithoutLeftovers = meals => reject(meals, 'leftOvers');
const mealsWithRecipe = meals => filter(meals, 'recipeId');
const mealsHasSwaps = meals => some(meals, 'originalRecipeId');

// Selectors
export const selectMealPlans = state => {
  return state.entities.mealPlans || {};
};

export const selectRecipes = state => {
  return state.entities.recipes;
};

export const selectMealPlan = (state, mealPlanId) => {
  return mealPlanId ? selectMealPlans(state)[mealPlanId] || {} : {};
};

export const selectMealPlanIdForWeek = (state, weekId) => {
  const { current } = state.entities;
  return current ? current[weekId] : undefined;
};

export const selectMealPlanIsClone = (state, mealPlanId) => {
  const mealPlan = selectMealPlan(state, mealPlanId);
  return mealPlan.associatedId && mealPlan.associatedType ? true : false;
};

export const selectMealPlanTitle = (state, mealPlanId, weekNumber) => {
  const { title } = selectMealPlan(state, mealPlanId);
  return title || 'Meal Plan';
};

export const selectMealPlanList = (state, mealPlanId) => {
  const decoratedMeals = meals => {
    return map(meals, (meal, index) => {
      return {
        ...meal,
        day: meal.day === 0 ? 7 : meal.day,
        mealsInGroup: index === 0 ? meals.length : 0,
      };
    });
  };

  return flatMap(selectDays(state, mealPlanId), day =>
    decoratedMeals(mealsWithRecipe(day.meals))
  );
};

export const selectMealPlanPdfs = (state, mealPlanId, subscriptionId) => {
  const mealPlan = selectMealPlan(state, mealPlanId);

  if (mealPlan.id && subscriptionId) {
    const prepend = `${
      hosts.api
    }/v1/voome/subscriptions/${subscriptionId}/meal-plans/${mealPlan.id}`;

    return {
      mealPlan: `${prepend}/print`,
      recipes: `${prepend}/print-recipes`,
      shoppingList: `${prepend}/shopping-list`,
    };
  }
};

export const selectMealPlansByType = (state, type, page = 1) => {
  const { result } = selectPaginationByType(state, type, page);
  return map(result, id => selectMealPlan(state, id));
};

export const selectMealPlansCurrent = (state, page = 1) => {
  return selectMealPlansByType(state, 'current', page);
};

export const selectMealsHasCookup = (state, mealPlanId) => {
  return some(mealsFlat(selectMealPlan(state, mealPlanId)), 'cookup');
};

export const selectPaginationByType = (state, type, page = 1) => {
  return state.entities[type] ? state.entities[type][page] || {} : {};
};

export const selectDays = (state, mealPlanId) => {
  const recipes = selectRecipes(state);
  const { days } = selectMealPlan(state, mealPlanId);

  const calculateCalories = (sum, meal) => {
    const recipe = recipes[meal.recipeId];
    return sum + (recipe.caloriesPerServe || 0);
  };

  const recipe = meal => {
    return {
      ...meal,
      recipe: recipes[meal.recipeId],
      originalRecipe: recipes[meal.originalRecipeId],
    };
  };

  const dayWithMeals = day => {
    return {
      ...day,
      calories: reduce(mealsWithRecipe(day.meals), calculateCalories, 0),
      meals: map(day.meals, recipe),
    };
  };

  return map(days, dayWithMeals);
};

export const selectShoppingListRecipes = (state, mealPlanId) => {
  const mealsFlow = flow([mealsFlat, mealsWithoutLeftovers, mealsWithRecipe]);

  return map(
    mealsFlow(selectMealPlan(state, mealPlanId)),
    meal => selectRecipes(state)[meal.recipeId]
  );
};

export const selectPageForMealPlanId = (state, mealPlanId, type) => {
  const resultHasMealPlan = item => item.result.indexOf(mealPlanId) > -1;
  return findKey(state.entities[type], resultHasMealPlan);
};

export const selectHasMealPlanRecipe = (state, mealPlanId) => {
  return some(
    flatMap(selectDays(state, mealPlanId), day => day.meals),
    'recipeId'
  );
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  const reduceEntities = (stateEnities, payloadEntities) => {
    return {
      ...stateEnities,
      byVoome: {
        ...stateEnities.byVoome,
        ...payloadEntities.byVoome,
      },
      mine: {
        ...stateEnities.mine,
        ...payloadEntities.mine,
      },
      mealPlans: {
        ...stateEnities.mealPlans,
        ...payloadEntities.mealPlans,
      },
      recipes: {
        ...stateEnities.recipes,
        ...payloadEntities.recipes,
      },
    };
  };

  switch (type) {
    case FETCH:
    case FETCH_SWAPS:
      return {
        ...state,
        isLoading: true,
        level: payload.level,
      };

    case FETCH_WEEK:
      return {
        ...state,
        isLoading: true,
        level: payload.level,
        active: null,
      };

    case FETCH_ACTIVE:
    case FETCH_ALL:
    case DELETE:
    case UPDATE:
      return {
        ...state,
        isLoading: true,
      };

    case ACTIVATE:
    case DEACTIVATE:
      return {
        ...state,
        isActivating: true,
      };

    case CLONE:
    case CREATE:
      return {
        ...state,
        isCreating: true,
        result: null,
      };

    case RESET:
    case SWAP:
    case SWAP_REVERT:
      return {
        ...state,
        isSwapping: true,
      };

    case CREATE_MEAL:
    case DELETE_MEAL:
      return {
        ...state,
        isSwapping: true,
        isSwappingDay: payload.day,
      };

    // Success
    case FETCH_SUCCESS:
    case CREATE_SUCCESS:
    case CLONE_SUCCESS:
    case CREATE_MEAL_SUCCESS: // Might need to move this.
      return {
        ...state,
        isCreating: false,
        isLoading: false,
        isSwapping: false,
        isSwappingDay: -1,
        result: payload.result,
        entities: reduceEntities(state.entities, payload.entities),
      };

    case FETCH_WEEK_SUCCESS:
      return {
        ...state,
        isLoading: false,
        entities: {
          ...state.entities,
          current: {
            ...state.entities.current,
            [payload.weekId]: payload.mealPlanId,
          },
        },
      };

    case FETCH_ACTIVE_SUCCESS:
      const entities = state.entities.mealPlans[payload.result]
        ? state.entities
        : reduceEntities(state.entities, payload.entities);

      return {
        ...state,
        isLoading: false,
        active: payload.result,
        entities,
      };

    case FETCH_ALL_SUCCESS:
      return {
        ...state,
        isLoading: false,
        result: null,
        entities: {
          ...reduceEntities(state.entities, payload.entities),
          [payload.key]: {
            ...state.entities[payload.key],
            ...payload.entities[payload.key],
          },
        },
      };

    case ACTIVATE_SUCCESS:
    case DEACTIVATE_SUCCESS:
      return {
        ...state,
        isActivating: false,
        active: payload.mealPlanId,
      };

    case DELETE_MEAL_SUCCESS:
      const { day, mealPlanId, mealPlanRecipeId } = action.payload;
      const mealPlan = state.entities.mealPlans[mealPlanId];
      let recipeId;

      const days = map(mealPlan.days, dayInfo => {
        if (dayInfo.day === day) {
          recipeId = find(dayInfo.meals, { id: mealPlanRecipeId }).recipeId;
          return {
            ...dayInfo,
            meals: reject(dayInfo.meals, { id: mealPlanRecipeId }),
          };
        } else {
          return dayInfo;
        }
      });

      return {
        ...state,
        isSwapping: false,
        isSwappingDay: -1,
        entities: {
          ...state.entities,
          mealPlans: {
            ...state.entities.mealPlans,
            [mealPlanId]: {
              ...mealPlan,
              days,
              recipeCount: mealPlan.recipeCount - 1,
              recipes: reject(mealPlan.recipes, id => id === recipeId),
            },
          },
        },
      };

    case DELETE_SUCCESS:
      // Destructuring technique to create a new immutable mealPlan with key
      // removed, key must be a string to work - go figure.
      const key = `${payload.mealPlanId}`;
      const { [key]: undefined, ...mealPlans } = state.entities.mealPlans;

      return {
        ...state,
        isLoading: false,
        entities: {
          ...state.entities,
          mealPlans,
          mine: null,
        },
      };

    case UPDATE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        entities: {
          ...state.entities,
          mealPlans: {
            ...state.entities.mealPlans,
            [payload.mealPlanId]: {
              ...state.entities.mealPlans[payload.mealPlanId],
              ...payload.mealPlan,
            },
          },
        },
      };

    // Fail
    case ERROR:
      return {
        ...state,
        isCreating: false,
        isLoading: false,
        isSwapping: false,
        isSwappingDay: -1,
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/logic/mealPlan.js